import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messages", "input"]
  static values = {
    sessionId: Number,
    muted: { type: Boolean, default: false },
    postUrl: String,
    paramKey: { type: String, default: "live_session_message" },
    currentUserId: { type: Number, default: 0 },
    role: { type: String, default: "student" },
    deleteConfirm: { type: String, default: "Supprimer ce message ?" }
  }

  connect() {
    this.classifyAllMessages()
    this.scrollToBottom()
    this.observeNewMessages()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  // --- Envoi de message ---

  async sendMessage(event) {
    event.preventDefault()

    if (this.mutedValue) return

    const content = this.inputTarget.value.trim()
    if (!content) return

    this.inputTarget.value = ""
    this.inputTarget.disabled = true

    try {
      const response = await fetch(this.postUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ [this.paramKeyValue]: { content } })
      })

      if (!response.ok) {
        console.error("[LiveSessionChat] Erreur envoi message:", response.status)
      }
    } catch (error) {
      console.error("[LiveSessionChat] Erreur reseau:", error)
    } finally {
      this.inputTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  // --- Classification propre/autre (style WhatsApp) ---

  classifyAllMessages() {
    if (!this.hasMessagesTarget || !this.currentUserIdValue) return

    const messages = this.messagesTarget.querySelectorAll(".student-chat-msg")
    messages.forEach(msg => this.classifyMessage(msg))
  }

  classifyMessage(element) {
    if (!element.classList.contains("student-chat-msg")) return
    // Éviter de reclassifier un message déjà traité
    if (element.classList.contains("student-chat-msg--own") ||
        element.classList.contains("student-chat-msg--other")) return

    const userId = parseInt(element.dataset.userId, 10)
    const isOwn = userId === this.currentUserIdValue

    element.classList.add(isOwn ? "student-chat-msg--own" : "student-chat-msg--other")

    // Afficher le bouton de suppression pour les formateurs
    if (this.roleValue === "trainer") {
      const deleteBtn = element.querySelector("[data-delete-btn]")
      if (deleteBtn) deleteBtn.classList.remove("hidden")
    }
  }

  // --- Auto-scroll ---

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  // Observe les nouveaux messages ajoutes au DOM (via Turbo Stream)
  observeNewMessages() {
    if (!this.hasMessagesTarget) return

    this.observer = new MutationObserver((mutations) => {
      let newMessageCount = 0
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.classifyMessage(node)
            // Ne pas compter ses propres messages dans le badge
            const userId = parseInt(node.dataset?.userId, 10)
            if (!userId || userId !== this.currentUserIdValue) {
              newMessageCount++
            }
          }
        }
      }
      this.scrollToBottom()

      if (newMessageCount > 0) {
        this.dispatch("newMessage", { detail: { count: newMessageCount } })
      }
    })

    this.observer.observe(this.messagesTarget, { childList: true })
  }

  // --- Suppression de message (trainer) ---

  async deleteMessage(event) {
    event.preventDefault()

    if (!window.confirm(this.deleteConfirmValue)) return

    const button = event.currentTarget
    const messageId = button.dataset.messageId
    const deleteUrl = `${this.postUrlValue}/${messageId}`

    try {
      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        console.error("[LiveSessionChat] Erreur suppression message:", response.status)
      }
    } catch (error) {
      console.error("[LiveSessionChat] Erreur reseau:", error)
    }
  }

  // --- Helpers ---

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
